import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, FormHelperText, Grid, IconButton,
  LinearProgress, MenuItem, OutlinedInput, Select, TextField, Typography, Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LinkIcon from "@mui/icons-material/Link";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageIcon from "@mui/icons-material/Image";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import DescriptionIcon from "@mui/icons-material/Description";
import { v4 as uuidv4 } from "uuid";

import TipTapEditorComponent from "../../components/Common/TipTapEditorComponent";
import { createTutorialAction, updateTutorialAction, uploadKnowledgeBaseAttachmentsAction } from "../../Actions/KnowledgeBaseAction";

const MAX_IMAGES = 5;
const MAX_VIDEOS = 5;
const MAX_DOCS = 5;
const MAX_IMAGE_MB = 5;
const MAX_VIDEO_MB = 200;
const MAX_DOC_MB = 20;
const IMAGE_EXTS = ["jpg", "jpeg", "png", "webp", "gif"];
const VIDEO_EXTS = ["mp4", "webm", "ogg", "mov", "m4v", "avi", "mkv"];
const DOC_EXTS = ["pdf", "doc", "docx", "xls", "xlsx", "txt", "zip"];

const getExt = (name = "") => name.split(".").pop().toLowerCase();
const getMediaType = (name) => {
  const ext = getExt(name);
  if (IMAGE_EXTS.includes(ext)) return "image";
  if (VIDEO_EXTS.includes(ext)) return "video";
  if (DOC_EXTS.includes(ext)) return "document";
  return null;
};
const mbOf = (bytes) => (bytes / 1024 / 1024).toFixed(1);

const isValidVideoUrl = (url) => {
  if (!url) return { valid: false, message: "URL is required" };
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return { valid: false, message: "Enter a valid URL" };
  }
  const hostname = parsed.hostname.toLowerCase();
  const isYouTube =
    hostname.includes("youtube.com") ||
    hostname.includes("youtu.be");
  if (isYouTube) return { valid: true };
  const pathname = parsed.pathname.toLowerCase();
  const ext = pathname.split(".").pop();
  if (VIDEO_EXTS.includes(ext)) return { valid: true };
  return {
    valid: false,
    message: `Only YouTube links or direct video URLs (${VIDEO_EXTS.map(e => "." + e).join(", ")}) are allowed`,
  };
};

const EMPTY_FORM = {
  moduleSlug: "",
  title: "",
  shortDescription: "",
  content: "",
  description: "",
};

const AttachIcon = ({ mediaType }) => {
  if (mediaType === "image") return <ImageIcon fontSize="small" sx={{ color: "#6366f1" }} />;
  if (mediaType === "video") return <VideoFileIcon fontSize="small" sx={{ color: "#0ea5e9" }} />;
  return <DescriptionIcon fontSize="small" sx={{ color: "#16a34a" }} />;
};

const KnowledgeBaseFormDialog = ({ open, onClose, onSaved, modules = [], tutorial = null, fixedModuleSlug = "" }) => {
  const isEdit = !!tutorial;
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [urlInput, setUrlInput] = useState({ show: false, url: "", label: "" });
  const [urlError, setUrlError] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      if (tutorial) {
        setForm({
          moduleSlug: tutorial.moduleSlug || "",
          title: tutorial.title || "",
          shortDescription: tutorial.shortDescription || "",
          content: tutorial.content || "",
          description: tutorial.description || "",
        });
        const existing = Array.isArray(tutorial.attachments) ? tutorial.attachments : [];
        setAttachments(existing.map((a) => ({ ...a, file: null, uploading: false })));
      } else {
        setForm({
          ...EMPTY_FORM,
          moduleSlug: fixedModuleSlug || "",
        });
        setAttachments([]);
      }
      setErrors({});
      setUrlInput({ show: false, url: "", label: "" });
      setUrlError("");
    }
  }, [open, tutorial]);

  const handleField = useCallback((e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  }, []);

  const countByType = useCallback(
    (type) => attachments.filter((a) => a.mediaType === type).length,
    [attachments]
  );

  const handleFilesSelected = async (files) => {
    if (!files?.length) return;
    const newEntries = [];
    const errs = [];
    let currentCounts = { image: countByType("image"), video: countByType("video"), document: countByType("document") };

    Array.from(files).forEach((file) => {
      const mediaType = getMediaType(file.name);
      if (!mediaType) { errs.push(`${file.name}: unsupported format`); return; }
      const maxMB = mediaType === "image" ? MAX_IMAGE_MB : mediaType === "video" ? MAX_VIDEO_MB : MAX_DOC_MB;
      const maxCount = mediaType === "image" ? MAX_IMAGES : mediaType === "video" ? MAX_VIDEOS : MAX_DOCS;
      if (file.size > maxMB * 1024 * 1024) { errs.push(`${file.name} exceeds ${maxMB}MB`); return; }
      if ((currentCounts[mediaType] || 0) >= maxCount) { errs.push(`Max ${maxCount} ${mediaType}s allowed`); return; }
      currentCounts[mediaType] = (currentCounts[mediaType] || 0) + 1;

      newEntries.push({
        id: uuidv4(),
        type: "file",
        mediaType,
        url: null,
        label: file.name,
        uploadStatus: "queued",
        file,
        uploading: mediaType !== "video",
        size: file.size
      });
    });

    if (errs.length) setErrors((e) => ({ ...e, attachments: errs.join(" | ") }));
    else setErrors((e) => ({ ...e, attachments: undefined }));

    if (!newEntries.length) return;

    setAttachments((prev) => [...prev, ...newEntries]);

    const staticFiles = newEntries.filter(a => a.mediaType !== "video");
    if (staticFiles.length > 0) {
      try {
        const uploadedUrls = await uploadKnowledgeBaseAttachmentsAction(staticFiles.map(a => a.file));
        setAttachments((prev) => {
          const updated = [...prev];
          staticFiles.forEach((sf, i) => {
            const idx = updated.findIndex(a => a.id === sf.id);
            if (idx !== -1) {
              if (uploadedUrls[i]) {
                updated[idx] = { ...updated[idx], uploading: false, url: uploadedUrls[i], uploadStatus: "completed" };
              } else {
                updated[idx] = { ...updated[idx], uploading: false, uploadStatus: "failed", error: "Upload failed" };
              }
            }
          });
          return updated;
        });
      } catch (err) {
        setAttachments((prev) => {
          const updated = [...prev];
          staticFiles.forEach(sf => {
            const idx = updated.findIndex(a => a.id === sf.id);
            if (idx !== -1) {
              updated[idx] = { ...updated[idx], uploading: false, uploadStatus: "failed", error: "Upload failed" };
            }
          });
          return updated;
        });
      }
    }
  };

  const removeAttachment = (id) => setAttachments((prev) => prev.filter((a) => a.id !== id));

  const addUrlAttachment = () => {
    const trimmedUrl = urlInput.url.trim();
    const check = isValidVideoUrl(trimmedUrl);
    if (!check.valid) { setUrlError(check.message); return; }
    setAttachments((prev) => [...prev, { id: uuidv4(), type: "url", mediaType: "video", url: trimmedUrl, label: urlInput.label.trim() || "Video Link", uploadStatus: "completed" }]);
    setUrlInput({ show: false, url: "", label: "" });
    setUrlError("");
  };

  const onDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const onDragLeave = () => setIsDragOver(false);
  const onDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files?.length) handleFilesSelected(e.dataTransfer.files);
  }, []);

  const validate = () => {
    const e = {};
    if (!form.moduleSlug) e.moduleSlug = "Module is required.";
    if (!form.title.trim()) e.title = "Title is required.";
    else if (form.title.trim().length > 150) e.title = "Max 150 characters.";
    if (form.shortDescription && form.shortDescription.length > 500) e.shortDescription = "Max 500 characters.";

    const stillUploading = attachments.some(a => a.uploading);
    if (stillUploading) e.attachments = "Please wait for files to finish uploading.";

    return e;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      const videoFiles = attachments.filter((a) => a.type === "file" && a.mediaType === "video" && a.file);
      const urlEntries = attachments.filter((a) => a.type === "url").map(({ file, uploading, ...rest }) => rest);

      const staticFileAttachments = attachments.filter((a) => a.mediaType !== "video" && a.url).map(({ file, uploading, ...rest }) => rest);

      const allStaticAttachments = [...urlEntries, ...staticFileAttachments];

      const payload = {
        moduleSlug: form.moduleSlug,
        title: form.title.trim(),
        shortDescription: form.shortDescription?.trim() || "",
        content: form.content || "",
        description: form.description || "",
        attachmentsJson: JSON.stringify(allStaticAttachments),
        ...(videoFiles.length ? { videoSourceType: "file" } : {}),
      };

      const primaryVideoFile = videoFiles[0]?.file || null;
      const result = isEdit
        ? await updateTutorialAction(tutorial.id, payload, primaryVideoFile)
        : await createTutorialAction(payload, primaryVideoFile);

      onSaved?.(result);
    } catch (err) {
      setErrors({ submit: err?.message || "Failed to save article. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        className="text-bg-primary"
        sx={{
          py: 2.5,
          px: 3,
          fontSize: "1.25rem",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>{isEdit ? "Update Knowledge Base" : "Create New Knowledge Base"}</span>
        <IconButton onClick={onClose} disabled={saving} size="small" sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <div>
        <DialogContent sx={{ px: 3, py: 3 }}>
        {/* Article Information Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: 2,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: "primary.main", mb: 2 }}
          >
            Knowledge Base Information
          </Typography>
          <Grid container spacing={2.5}>
            {/* Module */}
            {!fixedModuleSlug && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Module<span className="text-danger ms-1">*</span>
                </Typography>
                <FormControl fullWidth error={!!errors.moduleSlug} size="small">
                  <Select
                    displayEmpty
                    name="moduleSlug"
                    value={form.moduleSlug}
                    onChange={handleField}
                    input={<OutlinedInput />}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 250 } } }}
                  >
                    <MenuItem value="" disabled>
                      Select module
                    </MenuItem>
                    {modules.map((m) => (
                      <MenuItem key={m.slug} value={m.slug}>
                        {m.moduleName}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.moduleSlug && (
                    <FormHelperText error>{errors.moduleSlug}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            )}

            {/* Title */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Title<span className="text-danger ms-1">*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter article title"
                name="title"
                value={form.title}
                onChange={handleField}
                inputProps={{ maxLength: 150 }}
                error={!!errors.title}
                helperText={errors.title || `${form.title.length}/150`}
              />
            </Grid>

            {/* Short Description */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Short Description
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Short subtitle shown beneath the article title"
                name="shortDescription"
                value={form.shortDescription}
                onChange={handleField}
                multiline
                rows={2}
                inputProps={{ maxLength: 500 }}
                error={!!errors.shortDescription}
                helperText={
                  errors.shortDescription ||
                  `${form.shortDescription.length}/500`
                }
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Content Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: 2,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: "primary.main", mb: 2 }}
          >
            Knowledge Base Content
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Content
              </Typography>
              <TipTapEditorComponent
                key={`tiptap-${tutorial?.id ?? "new"}-${open}`}
                initialValue={form.content}
                disablePlugins={[]}
                tokens={[]}
                handleChange={(content) => setForm((f) => ({ ...f, content }))}
                handleBlur={(content) =>
                  setForm((f) => ({ ...f, content: content?.trim() || "" }))
                }
                isDisabled={saving}
                error={errors.content || null}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Attachments Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: "primary.main", mb: 0 }}
            >
              Attachments
            </Typography>
            {!urlInput.show && (
              <Button
                size="small"
                startIcon={<LinkIcon />}
                onClick={() =>
                  setUrlInput({ show: true, url: "", label: "" })
                }
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Add Video URL
              </Button>
            )}
          </Box>

          <Grid container spacing={2.5}>
            {/* URL Input */}
            {urlInput.show && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    alignItems: "flex-start",
                    p: 2,
                    bgcolor: "white",
                    border: "1px solid",
                    borderColor: "grey.200",
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ flex: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Video URL <Typography component="span" color="text.secondary" fontSize="inherit">(YouTube or .mp4/.webm/.mov link)</Typography>
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="https://youtube.com/watch?v=... or https://.../video.mp4"
                      value={urlInput.url}
                      onChange={(e) => {
                        setUrlInput((p) => ({ ...p, url: e.target.value }));
                        setUrlError("");
                      }}
                      error={!!urlError}
                      helperText={urlError}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Label (Optional)
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      value={urlInput.label}
                      onChange={(e) =>
                        setUrlInput((p) => ({ ...p, label: e.target.value }))
                      }
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "flex-end",
                      height: "100%",
                      pt: 2.75,
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={addUrlAttachment}
                      sx={{ boxShadow: "none", whiteSpace: "nowrap" }}
                    >
                      Add
                    </Button>
                    <IconButton
                      onClick={() => {
                        setUrlInput({ show: false, url: "", label: "" });
                        setUrlError("");
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Drag Drop Zone */}
            <Grid item xs={12}>
              <Box
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: "2px dashed",
                  borderColor: isDragOver
                    ? "primary.main"
                    : errors.attachments
                      ? "error.main"
                      : "grey.400",
                  bgcolor: isDragOver ? "primary.50" : "white",
                  borderRadius: 1,
                  p: 3,
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: "primary.50",
                  },
                }}
              >
                <input
                  type="file"
                  hidden
                  multiple
                  ref={fileInputRef}
                  onChange={(e) => {
                    if (e.target.files) handleFilesSelected(e.target.files);
                    e.target.value = null;
                  }}
                />
                <CloudUploadIcon
                  sx={{ fontSize: 40, color: "text.secondary", mb: 1 }}
                />
                <Typography variant="body1" fontWeight={500}>
                  Click or drag files here to upload
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  Images (&le;{MAX_IMAGE_MB}MB), Docs (&le;{MAX_DOC_MB}MB),
                  Videos (&le;{MAX_VIDEO_MB}MB)
                </Typography>
              </Box>
              {errors.attachments && (
                <FormHelperText error sx={{ mt: 1 }}>
                  {errors.attachments}
                </FormHelperText>
              )}
            </Grid>

            {/* Previews */}
            {attachments.length > 0 && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {attachments.map((a) => (
                    <Box
                      key={a.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1.5,
                        border: "1px solid",
                        borderColor: a.error
                          ? "error.main"
                          : "grey.200",
                        borderRadius: 1,
                        bgcolor: "white",
                      }}
                    >
                      <AttachIcon mediaType={a.mediaType} />
                      <Box sx={{ ml: 1.5, flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          noWrap
                        >
                          {a.label}
                        </Typography>
                        {a.uploading && (
                          <Box
                            sx={{
                              width: "100%",
                              mt: 1,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <LinearProgress sx={{ flex: 1 }} />
                            <Typography variant="caption" color="text.secondary">
                              Uploading...
                            </Typography>
                          </Box>
                        )}
                        {a.error && (
                          <Typography variant="caption" color="error">
                            {a.error}
                          </Typography>
                        )}
                        {!a.uploading && !a.error && a.size && (
                          <Typography variant="caption" color="text.secondary">
                            {mbOf(a.size)} MB
                          </Typography>
                        )}
                        {!a.uploading && a.type === "url" && (
                          <Typography
                            variant="caption"
                            color="primary"
                            sx={{ display: "block" }}
                            noWrap
                          >
                            {a.url}
                          </Typography>
                        )}
                        {!a.uploading &&
                          a.mediaType === "video" &&
                          a.type === "file" && (
                            <Typography variant="caption" color="warning.main">
                              Queued (uploads on save)
                            </Typography>
                          )}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => removeAttachment(a.id)}
                        sx={{ color: "error.main" }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Grid>
            )}

            {errors.submit && (
              <Grid item xs={12}>
                <Typography color="error" variant="body2" align="center">
                  {errors.submit}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      </DialogContent>

      <DialogActions className="border-top p-7 d-flex justify-content-between">
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="btn btn-sm btn-outline btn-outline-primary"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving || attachments.some((a) => a.uploading)}
          className="btn btn-sm btn-primary"
        >
          {isEdit ? "Update" : "Create"}  Knowledge Base
          {saving && (
            <span
              className="spinner-border spinner-border-sm ms-2"
              role="status"
              aria-hidden="true"
            ></span>
          )}
        </button>
      </DialogActions>
      </div>
    </Dialog>
  );
};

export default KnowledgeBaseFormDialog;
